// bidding-stage-entrance.js — the bidding stage inscribing itself the FIRST time
// the player enters the game. The gatehouse (the prize, at the sun) and the three
// battle standards (planted at the trick-slot centres) are stroke-based role icons
// that today POP into existence fully opaque while the cards deal in choreographed
// (full-table-deal.js). Here they instead ETCH in — a stroke-native, etch-only run
// of the Stamp (components/etch-stamp.js): the icons' own strokes draw on, the
// banner cloth blooms as a leftover fill ("pole, then cloth"), no announcement
// press (the stage is a setup, not a declaration). ANIMATIONS § BIDDING (The
// ceremony); the rollout site is the apply-etch-entrance initiative.
//
// FIRST INTRODUCTION ONLY. A significant icon arriving on the board for the first
// time earns the etch (the governing DESIGN principle); a next-round transition
// re-renders the same icons the player already knows, so it does NOT re-etch. The
// distinction is an ARM, exactly like the declarer-strip feat badges: the start
// screen arms the entrance when the player commits Play (game-start-session.js),
// and the FIRST round-start deal consumes it (full-table-deal.js). A next-round
// transition never arms → never etches; a reload/resize recovery render never
// routes through the start screen → never arms → never etches.
//
// The entrance does NOT own a timeline of its own — it hands the round-start deal
// a track to weave onto its master, so the inscription and the deal share ONE
// skip and ONE settle (the two-track race full-table-deal.js's header warns of).
// The stage inscribes as one quick, continuous motion (play-tuned snappy — fired
// exactly once, the first time the player enters a game):
//   1. the gatehouse inscribes first at the sun (the prize claiming centre stage);
//   2. the three standards then etch in TOGETHER — all in parallel, each seat's
//      position glyph fading in with its own banner and the two opponents' names
//      INSCRIBING on the same beat (the human carries no name).
// The going-around-the-table dealer-first stagger and the gatehouse-then-standards
// pause are both knobs (standardStaggerS, afterGatehouseS), defaulted to 0 so the
// whole stage reads as one inscribe rather than separable beats — a sub-100ms gap
// reads as stutter, not a beat. The card deal OVERLAPS the tail of the inscribe
// (full-table-deal.js's dealEtchOverlapS) rather than barriering on it: the cards
// fly while the inscribe is still finishing its tail (the opponent-name etch and
// strip fades outlast the quick icon strokes), so entrance and deal read as one
// continuous choreography. (An earlier cut barriered the deal behind the full etch;
// the inscribe was slow enough then that overlapping it read as busy — the speed-up
// is what made the overlap feel like one motion instead of clutter.)
import { gsap } from "../vendor/gsap.js";
import {
  ETCH_STAMP_DEFAULTS,
  prepareStamp,
  stageStamp,
  addStamp,
  shedStampResidue,
} from "./components/etch-stamp.js";

export const BIDDING_STAGE_ENTRANCE_DEFAULTS = {
  ...ETCH_STAMP_DEFAULTS,
  strokeNative: true, // the gatehouse walls + banner poles draw their own strokes; cloth blooms
  stampPress: false, // etch-only — the stage is a setup; no centre-stage announcement press
  stampScale: 1, // why: etch at rest size — no press to settle down from (the press-skip seam)
  etchS: 0.12, // play-test: each stroke's draw-on span — legible at ceremony size, a build-up the eye reads
  etchStaggerS: 0.045, // play-test: rhythm between an icon's own strokes (walls, then arch; pole, then cloth)
  inkS: 0.08, // play-test: the banner cloth's bloom
  afterGatehouseS: 0, // play-test: the beat after the gatehouse has fully inscribed before the standards begin. 0 = the banners begin the instant the gatehouse lands, so the whole stage reads as one continuous inscribe (a sub-100ms gap reads as stutter, not a beat); raise to ~0.1+ for a readable gatehouse-then-banners pause
  standardStaggerS: 0, // play-test: stagger between the three standards. 0 = all three (and their position strips) etch in PARALLEL as one beat — snappy over the going-around-the-table directional read; raise toward ~0.1 to make the dealer-first clockwise direction read again
  stripFadeS: 0.45, // play-test: each seat's position glyph (and pre-declaration cells) fades in over this span, starting with its own banner's etch
};

// The two opponents' names ENTER via the etch (IDENTITY § Where identity appears),
// not a fade — a clone-mode inscription over the Satoshi-Bold glyph outlines, the
// SAME idiom as the game-start "Lothar" wordmark (game-start-entrance.js's
// WORDMARK_ETCH_CONFIG). Etch-only (no press), settling to the strip's quiet rest.
const NAME_ETCH_CONFIG = {
  ...ETCH_STAMP_DEFAULTS,
  stampScale: 1, // why: etch at rest size — the name inscribes, never presses
  stampPress: false, // etch-only
  etchS: 0.28, // play-test: each letter's outline draw-on span (matches the logo wordmark)
  etchStaggerS: 0.05, // play-test: gap between letters — the inscription rhythm
  etchWidth: 11, // why: font units (1000upm em) — a hairline trace of the letter contour
};

const HOST_CLASS = "bid-stage-etch-host";

// Armed by the start screen on Play commit, consumed by the first round-start deal.
// Module-level animation-state exception (js/CLAUDE.md) — a single one-shot latch,
// the bidding-stage cousin of declaration-strip-etch.js's per-feat arm set.
let armed = false;

/** Arm the bidding-stage entrance: the next round-start deal etches the stage in.
 *  Called on the start-screen Play commit (game-start-session.js) — new game AND
 *  Continue, both "entering the game from the start screen". */
export function armBiddingStageEntrance() {
  armed = true;
}

/** Read-and-clear the arm. The first round-start deal calls this once; every later
 *  deal (next-round transitions) reads false → no re-etch. Consumed regardless of
 *  reduced-motion so the latch can't leak into a later round (full-table-deal.js
 *  calls this before its reduced-motion early return). */
export function consumeBiddingStageEntranceArm() {
  const was = armed;
  armed = false;
  return was;
}

// Wrap one icon's SVG in an ephemeral host the Stamp can drive (prepareStamp owns
// the host's `> svg`). `display: contents` so the host adds no layout box — the
// positioned .role-icon div keeps its inline centering transform untouched (the
// Stamp's shed would clobber a transform on its own host), and the SVG keeps the
// per-piece width/height attributes bidding-ui.js#roleIconConstellation stamped on it
// (§ Role Icons deliberately carries no generic CSS sizing arm).
// Shed + unwrapped at the settle, so the rested stage is exactly the factory SVGs.
function wrapEtchHost(svg) {
  const host = document.createElement("span");
  host.className = HOST_CLASS;
  host.style.display = "contents";
  svg.replaceWith(host);
  host.appendChild(svg);
  return host;
}

function unwrapEtchHost(host) {
  const svg = host.querySelector(":scope > svg");
  if (svg != null) host.replaceWith(svg);
}

// The three standards in inscription order: dealer-first, then clockwise around
// the table. Skat's clockwise play order IS DEALER → FOREHAND → MIDDLEHAND, so
// ordering by the banner's own data-game-position is rotation-invariant — it
// lands the dealer's standard first whichever screen slot it sits in this round
// (bidding-ui.js#appendBiddingBanner stamps the attribute). filter(Boolean) drops
// any missing seat (defensive; at first entry all three stand upright).
const CLOCKWISE_FROM_DEALER = ["DEALER", "FOREHAND", "MIDDLEHAND"];
function standardsInOrder(appEl) {
  const banners = [...appEl.querySelectorAll(".bid-banner")];
  return CLOCKWISE_FROM_DEALER.map((pos) =>
    banners.find((b) => b.dataset.gamePosition === pos),
  ).filter(Boolean);
}

// Wrap, stage hidden, and add one icon's etch-only Stamp to the master at `at`.
// Returns {host, stamp, dur} (dur = the etch's full span, so the caller can begin
// the standards once the gatehouse has fully inscribed), or null when no SVG.
function addIconEtch(master, iconDiv, at, c) {
  const svg = iconDiv.querySelector(":scope > svg");
  if (svg == null) return null;
  const host = wrapEtchHost(svg);
  const stamp = prepareStamp(host, c);
  stageStamp(host, stamp, c, { opacity: 1 });
  const sub = gsap.timeline();
  addStamp(sub, host, stamp, c, { opacity: 1 });
  master.add(sub, at);
  return { host, stamp, dur: sub.duration() };
}

// Reveal a seat's strip at its banner's beat: the position glyph (and any pre-
// declaration cells) FADES in; the opponent's name ENTERS via the etch. Both staged
// hidden SYNCHRONOUSLY (hide-before-await, like the icons) so neither flashes before
// its turn — the fade is opacity-only (the strip's centering transform must stay
// untouched), the etch stages the glyphs blank. Returns a cleanup that hands every
// touched node back to its render rest, run at the master's settle.
function addStripReveal(master, strip, at, c) {
  const cleanups = [];
  // The persona chip (the name row's identity mark) fades with the glyph strip —
  // the etch stays reserved for the name's inscription on this beat.
  const fades = [
    strip.querySelector(":scope > .position-strip-main"),
    strip.querySelector(".position-strip-persona"),
    // A human opponent's plain-text name (position-name.js fallback) can't etch —
    // it fades in with the persona chip on the same beat.
    strip.querySelector(".position-strip-name-plain"),
  ].filter((n) => n != null);
  for (const node of fades) {
    gsap.set(node, { autoAlpha: 0 });
    master.to(
      node,
      { autoAlpha: 1, duration: c.stripFadeS, ease: "power1.out" },
      at,
    );
    cleanups.push(() => gsap.set(node, { clearProps: "opacity,visibility" }));
  }
  const name = strip.querySelector(".position-strip-name");
  if (name != null) cleanups.push(addNameEtch(master, name, at));
  return () => cleanups.forEach((fn) => fn());
}

// Inscribe one opponent name onto the strip at `at`: wrap the glyph-word SVG in an
// ephemeral host, stage it blank, and add its clone-mode etch-only Stamp. Returns
// the shed (residue cleared, host unwrapped → the name rests as the factory SVG at
// its CSS quiet opacity). No measurable geometry (jsdom) → the Stamp degrades to a
// bloom; the shed still restores the rest.
function addNameEtch(master, nameSvg, at) {
  const host = wrapEtchHost(nameSvg);
  const stamp = prepareStamp(host, NAME_ETCH_CONFIG);
  stageStamp(host, stamp, NAME_ETCH_CONFIG, { opacity: 1 });
  const sub = gsap.timeline();
  addStamp(sub, host, stamp, NAME_ETCH_CONFIG, { opacity: 1 });
  master.add(sub, at);
  return () => {
    shedStampResidue(host, stamp);
    unwrapEtchHost(host);
  };
}

// Beat 2: each standard etches in at its dealer-clockwise offset, its seat's identity
// strip fading in on the same beat. Pushes each etched stamp onto `stamps`; returns the
// strip cleanups the settle runs (so the parent owns both lists in one place).
function weaveStandards(tl, appEl, standards, standardsAt, c, stamps) {
  const stripCleanups = [];
  standards.forEach((bannerDiv, i) => {
    const at = standardsAt + i * c.standardStaggerS;
    const pair = addIconEtch(tl, bannerDiv, at, c);
    if (pair != null) stamps.push(pair);
    const strip = appEl.querySelector(
      `.position-indicator[data-game-position="${bannerDiv.dataset.gamePosition}"]`,
    );
    if (strip != null) stripCleanups.push(addStripReveal(tl, strip, at, c));
  });
  return stripCleanups;
}

// Beat 1: the gatehouse inscribes alone, to full completion (it claims the sun).
// Returns the offset the standards then begin at — its full duration plus the
// after-gatehouse beat; 0 when there is no gatehouse (the standards lead).
function weaveGatehouse(tl, gatehouseDiv, c, stamps) {
  if (gatehouseDiv == null) return 0;
  const pair = addIconEtch(tl, gatehouseDiv, 0, c);
  if (pair == null) return 0;
  stamps.push(pair);
  return pair.dur + c.afterGatehouseS;
}

// The settle: shed each Stamp's draw/bloom residue and unwrap its host (banners rest
// as the factory SVGs, upright at final positions — what the bid replay / topple
// expect), then hand each faded-in strip back to its render rest.
function stageCleanup(stamps, stripCleanups) {
  return () => {
    for (const { host, stamp } of stamps) {
      shedStampResidue(host, stamp);
      unwrapEtchHost(host);
    }
    for (const fn of stripCleanups) fn();
  };
}

/**
 * Build the bidding-stage inscription as a track the round-start deal weaves onto
 * its master — the gatehouse inscribes first, then the three standards etch in
 * together (parallel by default; standardStaggerS reintroduces the dealer-first
 * clockwise sweep). Stages every icon hidden SYNCHRONOUSLY (hide-before-await), so
 * call in the same task as the render (full-table-deal.js does — exactly like the
 * deal stages its flown cards).
 *
 * @param {HTMLElement} appEl the #skat-app the freshly rendered stage lives in
 * @param {object} [config] BIDDING_STAGE_ENTRANCE_DEFAULTS + lab overrides, one flat bag
 * @returns {{tl: gsap.core.Timeline, cleanup: () => void}|null} null when the stage
 *   has no etchable icons (e.g. a preset-Ramsch entry with no bidding stage)
 */
export function buildBiddingStageEntranceTrack(appEl, config) {
  const c = { ...BIDDING_STAGE_ENTRANCE_DEFAULTS, ...config };
  const gatehouseDiv = appEl.querySelector("#bid-gatehouse");
  const standards = standardsInOrder(appEl);
  if (gatehouseDiv == null && standards.length === 0) return null;

  const tl = gsap.timeline();
  const stamps = [];
  // Beat 1 (gatehouse alone) sets where beat 2 (the standards) begins; the settle
  // sheds both lists' residue.
  const standardsAt = weaveGatehouse(tl, gatehouseDiv, c, stamps);
  const stripCleanups = weaveStandards(
    tl,
    appEl,
    standards,
    standardsAt,
    c,
    stamps,
  );
  if (stamps.length === 0) return null;
  return { tl, cleanup: stageCleanup(stamps, stripCleanups) };
}
